import {
  css, type SerializedStyles,
} from '@emotion/react';
import {
  type KeyboardEvent, type MouseEvent, useRef,
} from 'react';
import { ColorPickerComponent } from '~/boundary/settings/colorPicker/colorPicker.component';
import { COLOR_PICKER_HOVER_GROUP_CLASS_NAME } from '../colorPicker.constants';
import { type ColorResult } from '../colorPicker.types';
import { type ColorPickerHolderMethods } from '../colorPickerHolder/colorPickerHolder.component';
import { ColorPickerWithWheelComponent } from '../colorPickerWithWheel/colorPickerWithWheel.component';

const wrapperStyle = css({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
});

const colorPickerStyle = ({ spaceBetween }: {spaceBetween?: number}) => css({
  marginRight: `${spaceBetween ?? 15}px`,
});

const colorWheelStyle = css({
  width: 22,
  height: 22,
});

export type ColorPickerWithWheelAndTileProps = Readonly<{
  containerStyle?: SerializedStyles;
  isDisabled?: boolean;
  selectedColor: ColorResult;
  spaceBetween?: number;
  displayAlpha: boolean;

  onChange: (color: ColorResult) => void;
}>;

export const ColorPickerWithWheelAndTileComponent: React.FC<ColorPickerWithWheelAndTileProps> = ({
  containerStyle,
  displayAlpha,
  isDisabled,
  onChange,
  selectedColor,
  spaceBetween,
}) => {
  const wheelPickerRef = useRef<ColorPickerHolderMethods>(null);

  // Only clicks on the wrapper itself (the gap), clicks on the tile and wheel open their own picker
  const openPickerOnGapClick = (event: MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      wheelPickerRef.current?.openColorPicker();
    }
  };

  const handlePickerOnKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      wheelPickerRef.current?.openColorPicker();
    }
  };

  return (
    <div
      className={isDisabled ? undefined : COLOR_PICKER_HOVER_GROUP_CLASS_NAME}
      css={css(wrapperStyle, containerStyle)}
      onClick={isDisabled ? undefined : openPickerOnGapClick}
      onKeyDown={isDisabled ? undefined : handlePickerOnKeyDown}
      role={isDisabled ? undefined : 'button'}
      tabIndex={isDisabled ? undefined : 0}
    >
      <ColorPickerComponent
        css={colorPickerStyle({ spaceBetween })}
        onChange={onChange}
        selectedColor={selectedColor}
        editable={!isDisabled}
        displayAlpha={displayAlpha}
      />
      {!isDisabled && (
        <div css={colorWheelStyle}>
          <ColorPickerWithWheelComponent
            ref={wheelPickerRef}
            onChange={onChange}
            selectedColor={selectedColor}
            displayAlpha={displayAlpha}
          />
        </div>
      )}
    </div>
  );
};
